<template>
  <!-- ============================================ -->
  <!-- HERO SECTION - Diseño cálido y moderno inspirado en Coolliving -->
  <!-- ============================================ -->
  <section id="hero-section" class="hero-container relative min-h-0 lg:min-h-screen flex items-center overflow-hidden pb-8 lg:pb-0">
    <!-- Background cinematográfico con imagen de coliving -->
    <div class="absolute inset-0">
      <picture class="absolute inset-0 block w-full h-full">
        <source srcset="/images/hero-coliving-mobile.webp" media="(max-width: 768px)" type="image/webp">
        <source srcset="/images/hero-coliving.webp" media="(min-width: 769px)" type="image/webp">
        <img
          src="/images/hero-coliving.webp"
          alt=""
          width="1400"
          height="933"
          fetchpriority="high"
          decoding="async"
          class="absolute inset-0 w-full h-full object-cover object-center"
        >
      </picture>
      <div class="absolute inset-0 bg-gray-950/90"></div>
      <div class="absolute inset-0 opacity-[0.03]" style="background-image: url('data:image/svg+xml,%3Csvg viewBox=%220 0 256 256%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noise%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%224%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noise)%22/%3E%3C/svg%3E')"></div>
    </div>

    <!-- Contenedor principal B2C: izquierda mensaje + derecha buscador -->
    <div class="hero-page-gutter relative z-10 max-w-7xl mx-auto px-4 lg:px-8 pt-14 pb-6 lg:pt-20 lg:pb-16">
      <div class="flex flex-col gap-5 lg:grid lg:grid-cols-[1fr_1.15fr] lg:gap-14 lg:items-start min-w-0 max-w-full">

        <!-- IZQUIERDA: H1 + CTAs residente / gestor (desktop) -->
        <div class="space-y-4 lg:space-y-7 animate-slide-in-left">
          <div class="space-y-2 lg:space-y-4">
            <h1 class="hero-title">
              {{ heroMainTitle }}
            </h1>
            <p class="hero-subtitle-tagline">
              {{ heroTagline }}
            </p>
          </div>

          <div class="hidden lg:flex flex-col gap-4 w-full max-w-lg animate-fade-in">
            <button
              type="button"
              @click="goToColivingAdvancedSearch"
              class="hero-left-find-cta w-full inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white text-gray-900 font-semibold rounded-full text-base hover:bg-gray-100 transition-colors shadow-lg shadow-black/20"
            >
              {{ $t('heroLanding.audienceCta.find') }}
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>

            <div class="hero-mobile-manager">
              <h3 class="hero-mobile-manager-title">{{ $t('heroLanding.cards.manager.title') }}</h3>
              <p class="hero-mobile-manager-desc">{{ $t('heroLanding.cards.manager.description') }}</p>
              <div class="flex items-center gap-3 mt-3">
                <button
                  type="button"
                  @click="openHeroPartnerServicesModal"
                  class="flex-1 hero-mobile-manager-cta"
                >
                  <span>{{ $t('heroLanding.cards.manager.cta') }}</span>
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </button>
                <a
                  :href="HERO_MANAGER_INSTAGRAM_URL"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex-1 text-center hero-mobile-discover-link text-sm"
                >
                  {{ $t('heroLanding.cards.manager.discoverHow') }} ↗
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Panel: buscador + habitaciones reales -->
        <div
          id="hero-b2c-panel"
          class="hero-b2c-panel animate-slide-in-right w-full min-w-0 max-w-full lg:max-w-none lg:justify-self-end"
        >
          <h2 class="sr-only">{{ heroSeoH2Hidden }}</h2>

          <div class="hero-b2c-panel-inner min-w-0 max-w-full">
            <ColivingSearchBar
              v-model:selected-locations="selectedLocations"
              v-model:max-price="maxPrice"
              :available-locations="availableLocations"
              :is-loading="false"
              :no-border="true"
              :hide-price="true"
              :prominent-button="true"
              @submit="handleSearch"
              @apply-location-filter="applyLocationFilter"
            />

            <div v-if="regionId === 'uk'" class="mt-3">
              <router-link
                to="/findyourcoliving/london"
                class="hero-uk-link inline-flex items-center text-sm font-semibold hover:underline"
              >
                Browse {{ LONDON_SEO_LISTINGS_LABEL }} London coliving rooms →
              </router-link>
            </div>

            <div
              class="mt-3 lg:mt-4 relative min-w-0 max-w-full"
              @mouseenter="stopHeroCityAutoplay"
              @mouseleave="startHeroCityAutoplay"
              @focusin="stopHeroCityAutoplay"
              @focusout="startHeroCityAutoplay"
            >
              
              <div class="hero-featured-room-viewport">
                <div
                  class="hero-featured-room-track"
                  :style="{ transform: `translate3d(-${heroFeaturedSlideIndex * 100}%, 0, 0)` }"
                >
                  <div
                    v-for="slide in heroFeaturedSlides"
                    :key="slide.id"
                    class="hero-featured-room-slide"
                  >
                    <div
                      class="featured-room-card group cursor-pointer"
                      role="button"
                      tabindex="0"
                      @click="handleFeaturedRoomClick(slide)"
                      @keydown.enter="handleFeaturedRoomClick(slide)"
                    >
                      <div class="featured-room-media">
                        <img
                          :src="slide.image"
                          :alt="slide.listingTitle || slide.name"
                          class="featured-room-image"
                          loading="lazy"
                          decoding="async"
                        />
                        <div class="featured-room-gradient" />
                        <div class="featured-room-badges">
                          <span class="featured-room-badge featured-room-badge-light">{{ slide.name }}</span>
                        </div>
                      </div>
                      <div class="featured-room-footer">
                        <div class="min-w-0">
                          <p class="text-sm font-semibold text-gray-900 line-clamp-2">
                            {{ slide.listingTitle || slide.name }}
                          </p>
                          <p
                            v-if="slide.isLive && slide.listing?.propertyLocation"
                            class="text-xs text-gray-500 mt-0.5"
                          >
                            {{ slide.listing.propertyLocation }}
                          </p>
                          <p class="text-xs text-gray-500 mt-0.5">
                            <template v-if="slide.price != null">
                              {{ $t('heroLanding.cards.cities.from') }}
                              <span v-if="slide.currency === '$'">{{ slide.currency }}{{ slide.price }}</span>
                              <span v-else-if="slide.currency === '£'">{{ slide.currency }}{{ slide.price }}</span>
                              <span v-else>{{ slide.price }}{{ slide.currency }}</span>
                              /{{ $t('heroLanding.cards.cities.month') }}
                            </template>
                          </p>
                        </div>
                        <span class="featured-room-cta">
                          {{ $t('heroLanding.b2cPanel.viewInCity', { city: slide.name }) }}
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="featured-room-controls">
                <button
                  type="button"
                  class="featured-room-nav"
                  :aria-label="$t('heroLanding.b2cPanel.prevCity')"
                  @click="shiftHeroCity(-1)"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>
                </button>
                <div class="featured-room-dots" role="tablist">
                  <button
                    v-for="card in regionCards"
                    :key="`dot-${card.id}`"
                    type="button"
                    role="tab"
                    :aria-selected="selectedHeroCityId === card.id"
                    :class="['featured-room-dot', selectedHeroCityId === card.id ? 'featured-room-dot-active' : '']"
                    @click="selectHeroCity(card)"
                  />
                </div>
                <button
                  type="button"
                  class="featured-room-nav"
                  :aria-label="$t('heroLanding.b2cPanel.nextCity')"
                  @click="shiftHeroCity(1)"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
            </div>

            <ul class="hero-trust-row">
              <li>{{ $t('heroLanding.b2cPanel.trustNoFees') }}</li>
              <li>{{ $t('heroLanding.b2cPanel.trustVerified') }}</li>
              <li>{{ $t('heroLanding.b2cPanel.trustOneSearch') }}</li>
            </ul>

            <!-- Desktop: Buscar ahora + Descubre cómo (panel derecho) -->
            <div class="hero-resident-actions hidden lg:flex">
              <button
                type="button"
                @click="handleSearch"
                class="hero-resident-action-primary"
              >
                <span>{{ $t('heroLanding.cards.resident.cta') }}</span>
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
              <a
                :href="HERO_RESIDENT_INSTAGRAM_URL"
                target="_blank"
                rel="noopener noreferrer"
                class="hero-resident-action-link"
              >
                {{ $t('heroLanding.cards.resident.discoverHow') }} ↗
              </a>
            </div>
          </div>
        </div>

        <!-- Mobile: Busco coliving → Descubre cómo → Gestiono colivings -->
        <div class="lg:hidden flex flex-col gap-3 w-full animate-fade-in">
          <button
            type="button"
            @click="goToColivingAdvancedSearch"
            class="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white text-gray-900 font-semibold rounded-full text-sm hover:bg-gray-100 transition-colors shadow-lg shadow-black/25"
          >
            {{ $t('heroLanding.audienceCta.find') }}
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button>

          <a
            :href="HERO_RESIDENT_INSTAGRAM_URL"
            target="_blank"
            rel="noopener noreferrer"
            class="hero-mobile-discover-link text-center"
          >
            {{ $t('heroLanding.cards.resident.discoverHow') }} ↗
          </a>

          <div class="hero-mobile-manager">
            <h3 class="hero-mobile-manager-title">{{ $t('heroLanding.cards.manager.title') }}</h3>
            <p class="hero-mobile-manager-desc">{{ $t('heroLanding.cards.manager.description') }}</p>
            <div class="flex items-center gap-3 mt-3">
              <button
                type="button"
                @click="openHeroPartnerServicesModal"
                class="flex-1 hero-mobile-manager-cta"
              >
                <span>{{ $t('heroLanding.cards.manager.cta') }}</span>
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
              <a
                :href="HERO_MANAGER_INSTAGRAM_URL"
                target="_blank"
                rel="noopener noreferrer"
                class="flex-1 text-center hero-mobile-discover-link text-sm"
              >
                {{ $t('heroLanding.cards.manager.discoverHow') }} ↗
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ============================================ -->
  <!-- CARDS SECTION - Estilo "Meet our digital workers" de 11x.ai -->
  <!-- ============================================ -->
  <!-- COMENTADO: Section cards-section movida a mini versión dentro de "Busco coliving" -->
  <template v-if="false">
  <section id="cards-section" class="py-20 sm:py-28 bg-white">
    <div class="sm:px-6 lg:px-8">
      <!-- Section Header (estilo 11x) -->
      <div class="text-center mb-12 sm:mb-16 animate-fade-in">
        <h2 class="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-5 leading-tight tracking-tight">
          {{ $t('heroLanding.cards.sectionTitle') }}
        </h2>
        <p class="text-base sm:text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">
          {{ $t('heroLanding.cards.sectionDescription') }}
        </p>
      </div>

      <!-- Cards Grid (estilo Alice/Julian de 11x.ai) -->
      <div class="grid md:grid-cols-2 gap-8 lg:gap-12 max-w-4xl mx-auto">

        <!-- Card 1: Gestionar Colivings -->
        <div class="group rounded-2xl transition-all duration-700" :class="highlightCard === 'manage' ? 'bg-gray-50 ring-1 ring-gray-200 shadow-sm p-4 -m-4' : ''" id="card-manage">
          <!-- Image wrapper (estilo 11x: rounded, tall, con overlays) -->
          <div 
            class="relative rounded-2xl overflow-hidden cursor-pointer mb-6"
            @click="handleSignupClick"
          >
            <div class="aspect-[4/5] relative">
              <div 
                class="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-700 group-hover:scale-105"
                style="background-image: url('/images/rodeo-project-management-software-yKx0oD46yL0-unsplash.jpg')"
              ></div>
              <!-- Overlay sutil para legibilidad de badges -->
              <div class="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/10"></div>

              <!-- Badges superiores (estilo 11x tags) -->
              <div class="absolute top-4 left-4 right-4 flex items-start justify-between">
                <div class="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-sm">
                  <span class="text-sm font-semibold text-gray-900">{{ $t('heroLanding.cards.manage.title') }}</span>
                </div>
                
              </div>
            </div>
          </div>

          <!-- Contenido debajo (estilo 11x: nombre, divider, desc, arrow CTA) -->
          <div class="px-1">
            <div class="flex items-center gap-2 mb-1">
              <h3 class="text-2xl font-bold text-gray-900">
                {{ $t('heroLanding.cards.manage.title') }}
              </h3>
            </div>
            <div class="h-px bg-gray-200 my-4"></div>
            <p class="text-gray-500 leading-relaxed mb-3">
              {{ $t('heroLanding.cards.manage.description') }}
            </p>
            <p class="mb-5">
              <a 
                href="https://www.instagram.com/p/DTaPI3ViLlt/" 
                target="_blank" 
                rel="noopener noreferrer"
                class="text-indigo-600 hover:text-indigo-700 transition-colors font-medium text-sm"
              >{{ $t('heroLanding.cards.manage.discoverHow') }} ↗</a>
            </p>
            <div class="flex flex-wrap items-center gap-4">
              <button 
                @click="handleSignupClick"
                class="inline-flex items-center gap-2 text-gray-900 font-semibold hover:text-indigo-600 transition-colors group/link"
              >
                {{ $t('heroLanding.cards.manage.cta') }}
                <svg class="w-5 h-5 transition-transform group-hover/link:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
              <button 
                @click="router.push('/encuentra-tu-gestor')"
                class="text-gray-400 font-medium hover:text-gray-600 transition-colors text-sm"
              >
                {{ $t('heroLanding.cards.manage.haveProperty') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Card 2: Encontrar Coliving -->
        <div class="group rounded-2xl transition-all duration-700" :class="highlightCard === 'find' ? 'bg-gray-50 ring-1 ring-gray-200 shadow-sm p-4 -m-4' : ''" id="card-find">
          <!-- Image wrapper -->
          <div 
            class="relative rounded-2xl overflow-hidden cursor-pointer mb-6"
            @click="router.push('/encuentratucoliving')"
          >
            <div class="aspect-[4/5] relative">
              <div 
                class="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-700 group-hover:scale-105"
                style="background-image: url('https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800&q=80')"
              ></div>
              <div class="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/10"></div>

              <!-- Badges superiores -->
              <div class="absolute top-4 left-4 right-4 flex items-start justify-between">
                <div class="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-sm">
                  <span class="text-sm font-semibold text-gray-900">{{ $t('heroLanding.cards.find.title') }}</span>
                </div>
                
              </div>
            </div>
          </div>

          <!-- Contenido debajo -->
          <div class="px-1">
            <div class="flex items-center gap-2 mb-1">
              <h3 class="text-2xl font-bold text-gray-900">
                {{ $t('heroLanding.cards.find.title') }}
              </h3>
            </div>
            <div class="h-px bg-gray-200 my-4"></div>
            <p class="text-gray-500 leading-relaxed mb-3">
              {{ $t('heroLanding.cards.find.description') }}
            </p>
            <p class="mb-5">
              <a 
                href="https://www.instagram.com/p/DUDYvyWCLRs/" 
                target="_blank" 
                rel="noopener noreferrer"
                class="text-indigo-600 hover:text-indigo-700 transition-colors font-medium text-sm"
              >{{ $t('heroLanding.cards.find.discoverHow') }} ↗</a>
            </p>
            <button 
              @click="router.push(primarySearchPath)"
              class="inline-flex items-center gap-2 text-gray-900 font-semibold hover:text-indigo-600 transition-colors group/link"
            >
              {{ $t('heroLanding.cards.find.cta') }}
              <svg class="w-5 h-5 transition-transform group-hover/link:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          </div>
        </div>

      </div>
    </div>
  </section>
  </template>

  <!-- ============================================ -->
  <!-- B2C SECTION: Encuentra tu coliving -->
  <!-- ============================================ -->
  <template v-if="false">

  <section class="relative overflow-hidden bg-gray-950">
    <div class="absolute inset-0">
      <div class="absolute inset-0 bg-cover bg-center bg-no-repeat" style="background-image: url('https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1920&q=80');"></div>
      <div class="absolute inset-0 bg-gray-950/90"></div>
      <div class="absolute inset-0 bg-gradient-to-b from-gray-950 via-gray-950/80 to-gray-950/60"></div>
      <div class="absolute inset-0 opacity-[0.03]" style="background-image: url('data:image/svg+xml,%3Csvg viewBox=%220 0 256 256%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noise%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%224%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noise)%22/%3E%3C/svg%3E');"></div>
    </div>

    <div class="relative z-10 pt-14 sm:pt-16 pb-14 sm:pb-16 px-4 sm:px-6 lg:px-8">

      <!-- Cabecera -->
      <div class="text-center max-w-3xl mx-auto mb-10">
        
        <h2 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight tracking-tight">
          {{ $t('heroLanding.b2c.title') }}
        </h2>
        <p class="text-sm sm:text-base text-gray-300 leading-relaxed mb-8 max-w-xl mx-auto">
          {{ $t('heroLanding.b2c.subtitle') }}
        </p>
        <div class="flex flex-wrap justify-center gap-8 sm:gap-12">
          <div class="text-center">
            <div class="text-2xl sm:text-3xl font-bold text-white mb-0.5">6.900+</div>
            <div class="text-gray-300 text-xs font-semibold tracking-wide uppercase">{{ $t('heroLanding.b2c.stats.rooms') }}</div>
          </div>
          <div class="text-center">
            <div class="text-2xl sm:text-3xl font-bold text-white mb-0.5">0%</div>
            <div class="text-gray-300 text-xs font-semibold tracking-wide uppercase">{{ $t('heroLanding.stats.commissions') }}</div>
          </div>
          <div class="text-center">
            <div class="text-2xl sm:text-3xl font-bold text-white mb-0.5">14</div>
            <div class="text-gray-300 text-xs font-semibold tracking-wide uppercase">{{ $t('heroLanding.stats.cities') }}</div>
          </div>
          <div class="text-center">
            <div class="text-2xl sm:text-3xl font-bold text-white mb-0.5">350€</div>
            <div class="text-gray-300 text-xs font-semibold tracking-wide uppercase">{{ $t('heroLanding.b2c.stats.fromPerMonth') }}</div>
          </div>
        </div>
      </div>

      <!-- Grid de colivings destacados -->
      <div class="max-w-7xl mx-auto">
        <div class="featured-properties-grid mb-8">

          <div class="bg-white rounded-2xl overflow-hidden hover:shadow-2xl hover:scale-[1.03] transition-all duration-500 cursor-pointer group" @click="router.push('/encuentratucoliving/madrid')">
            <div class="aspect-[4/3] overflow-hidden relative">
              <div class="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style="background-image: url('https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800&q=80')"></div>
              <div class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            <div class="p-4">
              <p class="text-sm font-semibold text-gray-900 mb-1">{{ $t('heroLanding.featuredColivings.madrid.location') }}</p>
              <h3 class="font-semibold text-gray-600 line-clamp-1 text-sm mb-1">{{ $t('heroLanding.featuredColivings.madrid.title') }}</h3>
              <div class="flex items-center gap-1.5 text-sm text-gray-600 mb-1">
                <span class="font-medium">{{ $t('heroLanding.featuredColivings.madrid.rooms') }}</span>
                <span class="text-gray-400">·</span>
                <span>{{ $t('heroLanding.featuredColivings.madrid.bathrooms') }}</span>
              </div>
              <p class="text-sm font-semibold text-gray-900 mt-2">{{ $t('heroLanding.featuredColivings.madrid.price') }}<span class="font-normal text-gray-600 text-xs">{{ $t('heroLanding.featuredColivings.madrid.perMonth') }}</span></p>
            </div>
          </div>

          <div class="bg-white rounded-2xl overflow-hidden hover:shadow-2xl hover:scale-[1.03] transition-all duration-500 cursor-pointer group" @click="router.push('/encuentratucoliving/barcelona')">
            <div class="aspect-[4/3] overflow-hidden relative">
              <div class="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style="background-image: url('https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800&q=80')"></div>
              <div class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            <div class="p-4">
              <p class="text-sm font-semibold text-gray-900 mb-1">{{ $t('heroLanding.featuredColivings.barcelona.location') }}</p>
              <h3 class="font-semibold text-gray-600 line-clamp-1 text-sm mb-1">{{ $t('heroLanding.featuredColivings.barcelona.title') }}</h3>
              <div class="flex items-center gap-1.5 text-sm text-gray-600 mb-1">
                <span class="font-medium">{{ $t('heroLanding.featuredColivings.barcelona.rooms') }}</span>
                <span class="text-gray-400">·</span>
                <span>{{ $t('heroLanding.featuredColivings.barcelona.bathrooms') }}</span>
              </div>
              <p class="text-sm font-semibold text-gray-900 mt-2">{{ $t('heroLanding.featuredColivings.barcelona.price') }}<span class="font-normal text-gray-600 text-xs">{{ $t('heroLanding.featuredColivings.barcelona.perMonth') }}</span></p>
            </div>
          </div>

          <div class="bg-white rounded-2xl overflow-hidden hover:shadow-2xl hover:scale-[1.03] transition-all duration-500 cursor-pointer group" @click="router.push('/encuentratucoliving/valencia')">
            <div class="aspect-[4/3] overflow-hidden relative">
              <div class="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style="background-image: url('https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80')"></div>
              <div class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            <div class="p-4">
              <p class="text-sm font-semibold text-gray-900 mb-1">{{ $t('heroLanding.featuredColivings.valencia.location') }}</p>
              <h3 class="font-semibold text-gray-600 line-clamp-1 text-sm mb-1">{{ $t('heroLanding.featuredColivings.valencia.title') }}</h3>
              <div class="flex items-center gap-1.5 text-sm text-gray-600 mb-1">
                <span class="font-medium">{{ $t('heroLanding.featuredColivings.valencia.rooms') }}</span>
                <span class="text-gray-400">·</span>
                <span>{{ $t('heroLanding.featuredColivings.valencia.bathrooms') }}</span>
              </div>
              <p class="text-sm font-semibold text-gray-900 mt-2">{{ $t('heroLanding.featuredColivings.valencia.price') }}<span class="font-normal text-gray-600 text-xs">{{ $t('heroLanding.featuredColivings.valencia.perMonth') }}</span></p>
            </div>
          </div>

          <div class="bg-white rounded-2xl overflow-hidden hover:shadow-2xl hover:scale-[1.03] transition-all duration-500 cursor-pointer group" @click="router.push('/encuentratucoliving/sevilla')">
            <div class="aspect-[4/3] overflow-hidden relative">
              <div class="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style="background-image: url('https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80')"></div>
              <div class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            <div class="p-4">
              <p class="text-sm font-semibold text-gray-900 mb-1">{{ $t('heroLanding.featuredColivings.sevilla.location') }}</p>
              <h3 class="font-semibold text-gray-600 line-clamp-1 text-sm mb-1">{{ $t('heroLanding.featuredColivings.sevilla.title') }}</h3>
              <div class="flex items-center gap-1.5 text-sm text-gray-600 mb-1">
                <span class="font-medium">{{ $t('heroLanding.featuredColivings.sevilla.rooms') }}</span>
                <span class="text-gray-400">·</span>
                <span>{{ $t('heroLanding.featuredColivings.sevilla.bathrooms') }}</span>
              </div>
              <p class="text-sm font-semibold text-gray-900 mt-2">{{ $t('heroLanding.featuredColivings.sevilla.price') }}<span class="font-normal text-gray-600 text-xs">{{ $t('heroLanding.featuredColivings.sevilla.perMonth') }}</span></p>
            </div>
          </div>

        </div>

        <div class="text-center">
          <router-link
            to="/encuentratucoliving"
            class="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-gray-900 font-semibold rounded-full hover:bg-gray-100 transition-colors duration-200 text-sm"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            {{ $t('heroLanding.viewAllColivings') }}
          </router-link>
          <p class="mt-3 text-xs text-gray-400">{{ $t('heroLanding.b2c.trustNote') }}</p>
        </div>
      </div>

    </div>
  </section>
  </template>

  <!-- SEO Text Section -->
  <section class="py-12 bg-white">
    <div class="max-w-5xl mx-auto px-4 sm:px-6 text-center">
      <p class="text-lg text-gray-600 leading-relaxed">
        {{ $t('heroLanding.seoText.part1') }} <strong class="text-gray-900">{{ $t('heroLanding.seoText.part2') }}</strong> {{ $t('heroLanding.seoText.part3') }} <strong class="text-gray-900">{{ $t('heroLanding.seoText.part4') }}</strong> {{ $t('heroLanding.seoText.part5') }}
      </p>
    </div>
  </section>

</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { useAnalytics } from '@/composables/useAnalytics'
import { useRegion } from '@/composables/useRegion'
import { useHeroFeaturedListings, resolveHeroListingPath } from '@/composables/useHeroFeaturedListings'
import ColivingSearchBar from './ColivingSearchBar.vue'
import { SITE_METRICS } from '@/config/siteMetrics'
import { listingsLabel as LONDON_SEO_LISTINGS_LABEL } from '../../../shared/londonMarketStats.cjs'
import { openPartnerServicesModal } from '@/utils/partnerServicesModal'

const HERO_RESIDENT_INSTAGRAM_URL = 'https://www.instagram.com/p/DUDYvyWCLRs/'
const HERO_MANAGER_INSTAGRAM_URL = 'https://www.instagram.com/p/DTaPI3ViLlt/'

const { t, locale } = useI18n()

// Composable de región
const { 
  regionId, 
  regionCurrency, 
  regionMainCity,
  regionCities,
  formatPrice,
  initializeRegion 
} = useRegion()

const heroRegionKey = computed(() => {
  const id = regionId.value
  if (id === 'uk' || id === 'us') return id
  return 'es'
})

function translateHeroRegion(field) {
  const regionalKey = `heroLanding.hero.byRegion.${heroRegionKey.value}.${field}`
  const regional = t(regionalKey)
  if (regional !== regionalKey) return regional
  const fallbackKey = `heroLanding.hero.${field}`
  const fallback = t(fallbackKey)
  return fallback !== fallbackKey ? fallback : ''
}

function translateSeoH2Hidden() {
  const regionalKey = `heroLanding.seo.h2HiddenByRegion.${heroRegionKey.value}`
  const regional = t(regionalKey)
  if (regional !== regionalKey) return regional
  const fallbackKey = 'heroLanding.seo.h2Hidden'
  const fallback = t(fallbackKey)
  return fallback !== fallbackKey ? fallback : ''
}

const heroMainTitle = computed(() => translateHeroRegion('mainTitle'))
const heroTagline = computed(() => translateHeroRegion('tagline'))
const heroSeoH2Hidden = computed(() => translateSeoH2Hidden())

const primarySearchPath = computed(() => {
  if (regionId.value === 'uk') return '/findyourcoliving/london'
  if (regionId.value === 'us') return '/findyourcoliving/new-york'
  return '/encuentratucoliving'
})

function getSearchBasePath() {
  if (regionId.value === 'uk' || regionId.value === 'us') return '/findyourcoliving'
  return '/encuentratucoliving'
}

function getDefaultCitySlug() {
  if (regionId.value === 'uk') return 'london'
  if (regionId.value === 'us') return 'new-york'
  return null
}

function locationToSlug(name) {
  return name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '-')
}

// Estado de autenticación
const currentUser = ref(null)
const highlightCard = ref(null)

// Router para navegación
const router = useRouter()

// Analytics
const { trackHeroCarouselClick, trackHeroSearch } = useAnalytics()

// Configuración de cards por región
const regionCards = computed(() => {
  if (regionId.value === 'us') {
    // Nueva York — solo boroughs con colivings reales en Firestore
    return [
      {
        id: 'manhattan',
        name: 'Manhattan',
        searchLocation: 'Manhattan',
        filterLocation: 'Manhattan',
        image: 'https://images.unsplash.com/photo-1546436836-07a91091f160?w=400&q=80',
        price: 1800,
        currency: '$'
      },
      {
        id: 'brooklyn',
        name: 'Brooklyn',
        searchLocation: 'Brooklyn',
        filterLocation: 'Brooklyn',
        image: 'https://images.unsplash.com/photo-1518391846015-55a9cc003b25?w=400&q=80',
        price: 1500,
        currency: '$'
      },
      {
        id: 'queens',
        name: 'Queens',
        searchLocation: 'Queens',
        filterLocation: 'Queens',
        image: 'https://images.unsplash.com/photo-1568515387631-8b650bbcdb90?w=400&q=80',
        price: 1300,
        currency: '$'
      },
      {
        id: 'jersey-city',
        name: 'Jersey City',
        searchLocation: 'Jersey City',
        filterLocation: 'Jersey City',
        image: 'https://images.unsplash.com/photo-1555109307-f7d9da25c244?w=400&q=80',
        price: 1400,
        currency: '$'
      }
    ]
  }
  
  if (regionId.value === 'uk') {
    // Londres - 5 mejores barrios
    return [
      {
        id: 'shoreditch',
        name: 'Shoreditch',
        searchLocation: 'Shoreditch',
        filterLocation: 'Shoreditch',
        image: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=400&q=80',
        price: 1100,
        currency: '£'
      },
      {
        id: 'camden',
        name: 'Camden',
        searchLocation: 'Camden',
        filterLocation: 'Camden',
        image: 'https://images.unsplash.com/photo-1533929736458-ca588d08c8be?w=400&q=80',
        price: 1000,
        currency: '£'
      },
      {
        id: 'clapham',
        name: 'Clapham',
        searchLocation: 'Clapham',
        filterLocation: 'Clapham',
        image: 'https://images.unsplash.com/photo-1529655683826-aba9b3e77383?w=400&q=80',
        price: 950,
        currency: '£'
      },
      {
        id: 'hackney',
        name: 'Hackney',
        searchLocation: 'Hackney',
        filterLocation: 'Hackney',
        image: 'https://images.unsplash.com/photo-1543416080-79cfee5e8d7f?w=400&q=80',
        price: 1050,
        currency: '£'
      },
      {
        id: 'brixton',
        name: 'Brixton',
        searchLocation: 'Brixton',
        filterLocation: 'Brixton',
        image: 'https://images.unsplash.com/photo-1587974928442-77dc3e0dba72?w=400&q=80',
        price: 900,
        currency: '£'
      }
    ]
  }
  
  // España - ciudades actuales (por defecto)
  return [
    {
      id: 'madrid',
      name: t('heroLanding.cards.cities.madrid'),
      searchLocation: 'Madrid',
      filterLocation: 'Madrid',
      image: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400&q=80',
      price: 550,
      currency: '€'
    },
    {
      id: 'barcelona',
      name: t('heroLanding.cards.cities.barcelona'),
      searchLocation: 'Barcelona',
      filterLocation: 'Barcelona',
      image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&q=80',
      price: 750,
      currency: '€'
    },
    {
      id: 'valencia',
      name: t('heroLanding.cards.cities.valencia'),
      searchLocation: 'Valencia',
      filterLocation: 'Valencia',
      image: 'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=400&q=80',
      price: 500,
      currency: '€'
    },
    {
      id: 'canarias',
      name: t('heroLanding.cards.cities.canarias'),
      searchLocations: ['Las Palmas', 'Tenerife'],
      filterLocation: 'Las Palmas',
      image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=400&q=80',
      price: 450,
      currency: '€'
    },
    {
      id: 'malaga',
      name: t('heroLanding.cards.cities.malaga'),
      searchLocation: 'Málaga',
      filterLocation: 'Málaga',
      image: 'https://images.unsplash.com/photo-1540518614846-7eded433c457?w=400&q=80',
      price: 520,
      currency: '€'
    },
    {
      id: 'salamanca',
      name: t('heroLanding.cards.cities.salamanca'),
      searchLocation: 'Salamanca',
      filterLocation: 'Salamanca',
      image: 'https://images.unsplash.com/photo-1631679706909-1844bbd07221?w=400&q=80',
      price: 420,
      currency: '€'
    }
  ]
})

const {
  getDisplayedListing,
  onHeroCitySelected,
  isLoadingFeatured,
  isLoadingDeferred,
  isCityPoolLoading,
  seedInitialCity,
} = useHeroFeaturedListings(regionId, locale, () => regionCards.value, {
  priorityCount: 2,
  getLazyAnchor: () => document.getElementById('hero-b2c-panel'),
})

// Lista de barrios para US/UK (tags de acceso rápido — deben coincidir con propertyLocation en Firestore)
const regionNeighborhoods = computed(() => {
  if (regionId.value === 'us') {
    // Exactamente los 4 valores de propertyLocation que usan los scrapers NYC
    return [
      { id: 'manhattan', name: 'Manhattan' },
      { id: 'brooklyn', name: 'Brooklyn' },
      { id: 'queens', name: 'Queens' },
      { id: 'jersey-city', name: 'Jersey City' }
    ]
  }
  
  if (regionId.value === 'uk') {
    // Barrios representativos con colivings reales en Firestore
    return [
      { id: 'shoreditch', name: 'Shoreditch' },
      { id: 'canary-wharf', name: 'Canary Wharf' },
      { id: 'brixton', name: 'Brixton' },
      { id: 'hackney', name: 'Hackney' },
      { id: 'clapham', name: 'Clapham' }
    ]
  }
  
  return []
})

// Estado para el buscador de colivings
const selectedLocations = ref([])
const maxPrice = ref(null)

const selectedHeroCityId = ref('madrid')
let heroCityAutoplayTimer = null
const HERO_CITY_AUTOPLAY_MS = 7000
const MAX_HERO_FEATURED_DISPLAY_PRICE = 3500

function enrichFeaturedCityCard(base) {
  const live = getDisplayedListing(base.id)
  if (!live) {
    return {
      ...base,
      isLive: false,
      listingTitle: null,
      listing: null,
    }
  }
  let price = live.price ?? base.price
  if (price != null && price > MAX_HERO_FEATURED_DISPLAY_PRICE) {
    price = base.price
  }
  return {
    ...base,
    image: live.imageUrl || base.image,
    price,
    listingTitle: live.title,
    listing: live.raw,
    isLive: true,
  }
}

const heroFeaturedSlides = computed(() =>
  regionCards.value.map((base) => enrichFeaturedCityCard(base))
)

const heroFeaturedSlideIndex = computed(() => {
  const slides = heroFeaturedSlides.value
  if (!slides.length) return 0
  const idx = slides.findIndex((c) => c.id === selectedHeroCityId.value)
  return idx >= 0 ? idx : 0
})

const activeCityCard = computed(() => {
  const slides = heroFeaturedSlides.value
  if (!slides.length) {
    return {
      id: '',
      name: '',
      image: '',
      price: 0,
      currency: '€',
      isLive: false,
      listingTitle: null,
      listing: null,
    }
  }
  return slides.find((c) => c.id === selectedHeroCityId.value) || slides[0]
})

const isActiveCityListingLoading = computed(() => {
  const cityId = selectedHeroCityId.value
  if (getDisplayedListing(cityId)) return false
  return isLoadingFeatured.value || isLoadingDeferred.value || isCityPoolLoading(cityId)
})

function syncSelectedHeroCityFromRegion() {
  const first = regionCards.value[0]
  if (first) {
    selectedHeroCityId.value = first.id
    seedInitialCity(first.id)
  }
}

function selectHeroCity(card) {
  onHeroCitySelected(card.id)
  selectedHeroCityId.value = card.id
  selectedLocations.value = [card.filterLocation || card.searchLocation || card.name]
  stopHeroCityAutoplay()
  startHeroCityAutoplay()
}

function shiftHeroCity(delta) {
  const cards = regionCards.value
  if (!cards.length) return
  const idx = cards.findIndex((c) => c.id === selectedHeroCityId.value)
  const next = cards[(idx + delta + cards.length) % cards.length]
  selectHeroCity(next)
}

function startHeroCityAutoplay() {
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return
  }
  stopHeroCityAutoplay()
  heroCityAutoplayTimer = setInterval(() => shiftHeroCity(1), HERO_CITY_AUTOPLAY_MS)
}

function stopHeroCityAutoplay() {
  if (heroCityAutoplayTimer) {
    clearInterval(heroCityAutoplayTimer)
    heroCityAutoplayTimer = null
  }
}

/** CTA «Busco coliving» → ColivingAdvancedSearch (hub o ciudad según región y filtros del hero). */
function goToColivingAdvancedSearch() {
  handleSearch()
}

function openHeroPartnerServicesModal() {
  openPartnerServicesModal({
    source: 'hero_mobile_manager',
    serviceKey: 'marketplace',
  })
}

watch(regionId, () => {
  syncSelectedHeroCityFromRegion()
})

watch(
  () => regionCards.value.map((c) => c.id).join(','),
  () => syncSelectedHeroCityFromRegion()
)

// Manejar click en barrios (tags)
const handleNeighborhoodClick = (neighborhoodName) => {
  selectedLocations.value = [neighborhoodName]
  handleSearch()
}

// Provincias disponibles según región (deben coincidir con propertyLocation en Firestore)
const availableLocations = computed(() => {
  // Para España: todas las ciudades actuales
  if (regionId.value === 'es') {
    return [
      'Madrid', 'Barcelona', 'Valencia', 'Sevilla', 'Málaga', 'Las Palmas',
      'Tenerife', 'Salamanca', 'Alicante', 'Bilbao', 'Granada', 'Pamplona',
      'Getafe', 'Alcobendas'
    ]
  }
  // Para US: exactamente los valores de propertyLocation de los scrapers NYC
  if (regionId.value === 'us') {
    return ['Manhattan', 'Brooklyn', 'Queens', 'Jersey City']
  }
  // Para UK: barrios de Londres con colivings reales en Firestore
  if (regionId.value === 'uk') {
    return [
      'Canary Wharf', 'Wembley',
      'Hounslow', 'West Hampstead', 'Notting Hill', 'Marylebone',
      'Battersea', 'Harrow', 'Earlsfield',
      'Westbourne Park', 'Primrose Hill', 'Belsize Park',
      'Elephant and Castle', 'Clapham Common', 'Finsbury Park',
      'Maida Vale', 'Tooting', 'West Kensington',
      'Brixton', 'Limehouse',
      'Hammersmith', 'Mile End', 'Canada Water', 'Shoreditch', 'Bloomsbury', 'Kings Cross',
      'Walthamstow', 'Clapham Junction', 'Stockwell', 'East Putney', 'Fulham',
      'Hackney', 'Clapham', 'Camden',
      'Royal Docks', 'Canning Town', 'Poplar', 'Stepney Green', 'Lewisham'
    ]
  }
  // Por defecto: España
  return regionCities.value || ['Madrid', 'Barcelona']
})

// TRACKING: Signup de gestores con Google Ads
const handleSignupClick = () => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      'send_to': 'AW-17907163969/abFwCJirl-4bEMHG5tpC',
      'value': 25.0,
      'currency': 'EUR'
    })
    window.gtag('event', 'begin_signup', {
      'event_category': 'Gestores',
      'event_label': 'Landing Principal - CTA Hero Nuevo',
      'value': 25,
      'currency': 'EUR'
    })
  }
  router.push('/signup?userType=agent')
}

const handleLeadsMarketplaceClick = () => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'select_content', {
      content_type: 'b2b_leads',
      item_id: 'hero_leads_marketplace',
      event_category: 'Operadores',
      event_label: 'Landing Principal - CTA Leads Marketplace',
    })
  }
  router.push('/leads-marketplace')
}

// Scroll suave a cards section - COMENTADO (cards-section está comentada)
// const scrollToCards = () => {
//   const el = document.getElementById('cards-section')
//   if (el) el.scrollIntoView({ behavior: 'smooth' })
// }

// Manejar búsqueda de colivings
const handleSearch = () => {
  // Track búsqueda desde hero
  trackHeroSearch({
    ciudad: selectedLocations.value.join(',') || 'todas',
    isLoggedIn: !!currentUser.value,
  })
  
  let searchPath = getSearchBasePath()
  const defaultSlug = getDefaultCitySlug()

  if (selectedLocations.value.length === 1) {
    searchPath += `/${locationToSlug(selectedLocations.value[0])}`
  } else if (defaultSlug) {
    searchPath += `/${defaultSlug}`
  }
  
  const query = {}
  if (selectedLocations.value.length > 1) {
    query.locations = selectedLocations.value.join(',')
  }
  if (maxPrice.value) {
    query.maxPrice = maxPrice.value
  }
  
  router.push({ 
    path: searchPath, 
    query: Object.keys(query).length > 0 ? query : undefined 
  })
}

// Manejar click en carrusel de ciudades
const handleCarouselClick = (ciudad) => {
  trackHeroCarouselClick({ ciudad })
  router.push(`${getSearchBasePath()}/${ciudad.toLowerCase()}`)
}

function handleFeaturedRoomClick(card) {
  const target = card?.id ? card : activeCityCard.value
  selectHeroCity(target)
  if (target.isLive && target.listing) {
    const path = resolveHeroListingPath(target.listing, regionId.value)
    if (path) {
      trackHeroCarouselClick({ ciudad: target.id })
      router.push(path)
      return
    }
  }
  handleCarouselClick(target.id)
}

const applyLocationFilter = () => handleSearch()

onMounted(() => {
  initializeRegion()
  syncSelectedHeroCityFromRegion()
  startHeroCityAutoplay()

  window.addEventListener('region-changed', () => {
    syncSelectedHeroCityFromRegion()
  })

  const auth = getAuth()
  onAuthStateChanged(auth, (user) => {
    currentUser.value = user
  })
})

onUnmounted(() => {
  stopHeroCityAutoplay()
})

</script>

<style scoped>
/* Tipografías cargadas de forma async en index.html (no @import bloqueante) */

/* ═══════════════════════════════════════════════
   VARIABLES DE DISEÑO
   ═══════════════════════════════════════════════ */
:root {
  /* Paleta cálida inspirada en la imagen */
  --color-warm-cream: #F5EFE7;
  --color-warm-beige: #E8DED2;
  --color-dark-text: #1A1A1A;
  --color-accent-orange: #D84315;
  --color-accent-green: #2E7D32;
  --color-neutral-gray: #6B6B6B;
  --color-white: #FFFFFF;
  
  /* Tipografía */
  --font-display: 'Space Grotesk', system-ui, sans-serif;
  --font-body: 'DM Sans', system-ui, sans-serif;
  
  /* Espaciado */
  --spacing-xs: 0.5rem;
  --spacing-sm: 1rem;
  --spacing-md: 1.5rem;
  --spacing-lg: 2rem;
  --spacing-xl: 3rem;
  
  /* Sombras sutiles */
  --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.06);
  --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.08);
  --shadow-lg: 0 10px 30px rgba(0, 0, 0, 0.12);
}

/* ═══════════════════════════════════════════════
   CONTENEDOR PRINCIPAL DEL HERO
   ═══════════════════════════════════════════════ */
.hero-container {
  position: relative;
  overflow: hidden;
}

.hero-b2c-panel {
  min-width: 0;
  max-width: 100%;
}

.hero-b2c-panel-inner {
  background: transparent;
  border-radius: 0;
  padding: 0;
  box-shadow: none;
  border: none;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

@media (min-width: 1024px) {
  .hero-b2c-panel-inner {
    background: #ffffff;
    border-radius: 1.25rem;
    padding: 1.5rem;
    box-shadow: 0 24px 48px rgba(0, 0, 0, 0.22);
    border: 1px solid rgba(255, 255, 255, 0.08);
    overflow: hidden;
  }
}

.hero-room-meta {
  color: #99f6e4;
}

@media (min-width: 1024px) {
  .hero-room-meta {
    color: rgba(15, 118, 110, 0.85);
  }
}

.hero-uk-link {
  color: #99f6e4;
}

@media (min-width: 1024px) {
  .hero-uk-link {
    color: #115e59;
  }

  .hero-uk-link:hover {
    color: #042f2e;
  }
}

.stats-grid-compact .stat-value {
  font-size: clamp(1.25rem, 4vw, 1.75rem);
}

.stats-grid-compact .stat-label {
  font-size: 0.625rem;
}

.hero-featured-room-viewport {
  overflow: hidden;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  border-radius: 1rem;
}

.hero-featured-room-track {
  display: flex;
  width: 100%;
  transition: transform 0.95s cubic-bezier(0.45, 0, 0.2, 1);
  will-change: transform;
}

.hero-featured-room-slide {
  flex: 0 0 100%;
  width: 100%;
  min-width: 0;
}

@media (prefers-reduced-motion: reduce) {
  .hero-featured-room-track {
    transition: none;
  }
}

.featured-room-card {
  border-radius: 1rem;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  background: #fff;
  transition: box-shadow 0.25s ease;
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.28);
}

@media (min-width: 1024px) {
  .featured-room-card {
    box-shadow: none;
  }
}

.featured-room-card:hover {
  box-shadow: 0 12px 28px rgba(15, 118, 110, 0.15);
}

.featured-room-media {
  position: relative;
  aspect-ratio: 16 / 10;
  overflow: hidden;
}

.featured-room-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.featured-room-card:hover .featured-room-image {
  transform: scale(1.04);
}

.featured-room-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.45), transparent 55%);
  pointer-events: none;
}

.featured-room-badges {
  position: absolute;
  left: 0.75rem;
  bottom: 0.75rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.featured-room-badge {
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  background: #0f766e;
  color: #fff;
}

.featured-room-badge-light {
  background: rgba(255, 255, 255, 0.92);
  color: #134e4a;
}

.featured-room-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
}

.featured-room-cta {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #0f766e;
  white-space: nowrap;
}

.featured-room-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.featured-room-nav {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  border: 1px solid #e5e7eb;
  color: #374151;
  background: #fff;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.featured-room-nav:hover {
  border-color: #0f766e;
  color: #0f766e;
  background: #f0fdfa;
}

.featured-room-dots {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 12rem;
}

.featured-room-dot {
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 9999px;
  background: #d1d5db;
  transition: background 0.2s ease, transform 0.2s ease;
}

.featured-room-dot-active {
  background: #0f766e;
  transform: scale(1.25);
}

.hero-trust-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid #f3f4f6;
  list-style: none;
  padding-left: 0;
  margin-bottom: 0;
}

.hero-trust-row li {
  font-size: 0.6875rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.72);
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.hero-trust-row li::before {
  content: '';
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 9999px;
  background: #5eead4;
  flex-shrink: 0;
}

@media (min-width: 1024px) {
  .hero-trust-row li {
    color: #6b7280;
  }

  .hero-trust-row li::before {
    background: #14b8a6;
  }
}

.hero-resident-actions {
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f3f4f6;
}

.hero-resident-action-primary {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  border-radius: 9999px;
  border: 1px solid #9ca3af;
  background: #fff;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.hero-resident-action-primary:hover {
  border-color: #0f766e;
  box-shadow: 0 4px 12px rgba(15, 118, 110, 0.12);
}

.hero-resident-action-link {
  flex: 1;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  transition: color 0.2s ease;
}

.hero-resident-action-link:hover {
  color: #0f766e;
}

.hero-mobile-discover-link {
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
  transition: color 0.2s ease;
}

.hero-mobile-discover-link:hover {
  color: #fff;
}

.hero-mobile-manager {
  margin-top: 0.25rem;
  padding: 0.875rem 1rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: rgba(0, 0, 0, 0.22);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.hero-mobile-manager-title {
  font-size: 1.0625rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.3;
}

.hero-mobile-manager-desc {
  margin-top: 0.375rem;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.78);
}

.hero-mobile-manager-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0.625rem 0.875rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #fff;
  border-radius: 9999px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  background: transparent;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.hero-mobile-manager-cta:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.65);
}

@media (min-width: 1024px) {
  .hero-left-find-cta {
    min-height: 3.25rem;
    max-width: 100%;
  }

  .hero-mobile-manager {
    margin-top: 0;
  }
}

/* ═══════════════════════════════════════════════
   TÍTULO PRINCIPAL (H1)
   ═══════════════════════════════════════════════ */
.hero-title {
  font-family: var(--font-display);
  font-weight: 700;
  color: #FFFFFF;
  line-height: 1.15;
  letter-spacing: -0.02em;
  font-size: clamp(2.5rem, 7vw, 5rem);
  max-width: 40rem;
  min-height: 2.875rem; /* ~3 líneas mínimo para consistencia entre idiomas */
}

/* ═══════════════════════════════════════════════
   SUBTÍTULO / TAGLINE
   ═══════════════════════════════════════════════ */
.hero-subtitle-tagline {
  font-family: var(--font-body);
  font-size: clamp(1rem, 2vw, 1.25rem);
  font-weight: 500;
  color: #D1D5DB; /* text-gray-300 */
  line-height: 1.4;
  max-width: 32rem;
  min-height: 1.4rem; /* Altura mínima para consistencia */
}

.hero-inventory-badge {
  font-family: var(--font-body);
  font-size: 0.8125rem;
  font-weight: 600;
  color: #A5B4FC;
  letter-spacing: 0.02em;
  padding: 0.5rem 0.875rem;
  border-radius: 9999px;
  border: 1px solid rgba(165, 180, 252, 0.25);
  background: rgba(99, 102, 241, 0.12);
  display: inline-block;
  max-width: fit-content;
}

/* ═══════════════════════════════════════════════
   STATS GRID (1.500€, 95%, 15x)
   ═══════════════════════════════════════════════ */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  padding: 1rem 0;
}

.stat-item {
  text-align: left;
}

.stat-value {
  font-family: var(--font-display);
  font-size: clamp(1.75rem, 3vw, 2.25rem);
  font-weight: 700;
  color: #FFFFFF;
  line-height: 1;
  margin-bottom: 0.25rem;
}

.stat-label {
  font-family: var(--font-body);
  font-size: 0.7rem;
  font-weight: 600;
  color: #9CA3AF; /* text-gray-400 */
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* ═══════════════════════════════════════════════
   OPTION CARDS HORIZONTAL (con imagen lateral)
   ═══════════════════════════════════════════════ */
.option-card-horizontal {
  background: var(--color-white);
  border-radius: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  text-decoration: none;
  display: grid;
  grid-template-columns: 1fr 180px;
  overflow: hidden;
  min-height: 160px;
  position: relative;
  z-index: 5;
}

.option-card-horizontal:hover {
  border-color: rgba(99, 102, 241, 0.3);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

/* Card sin imagen (full width) */
.option-card-full {
  background: var(--color-white);
  border-radius: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  text-decoration: none;
  display: block;
  overflow: visible;
  min-height: 180px;
  position: relative;
  z-index: 1;
}

.option-card-full:hover {
  border-color: rgba(0, 0, 0, 0.08);
  box-shadow: none;
}

/* Card destacada (Busco coliving) - z-index más alto para el dropdown */
.option-card-featured {
  background: linear-gradient(135deg, #FFFFFF 0%, #F8F9FF 100%);
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  min-height: 150px;
  z-index: 10;
}

.option-card-featured:hover {
  border-color: rgba(0, 0, 0, 0.08);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.option-card-content {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.option-card-content-full {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: relative;
  z-index: 1;
}

.option-card-header {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.option-card-title {
  font-family: var(--font-body);
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-dark-text);
  line-height: 1.3;
}

.option-card-description {
  font-family: var(--font-body);
  font-size: 0.75rem;
  font-weight: 400;
  color: #6B7280;
  line-height: 1.4;
  min-height: 3rem; /* ~3-4 líneas mínimo para consistencia entre idiomas */
}

.option-card-image-wrapper {
  position: relative;
  overflow: hidden;
  width: 150px;
  min-height: 100%;
}

.option-card-image {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  transition: transform 0.7s cubic-bezier(0.4, 0, 0.2, 1);
}

.option-card-image::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.1), transparent);
}

/* Versión mobile de option cards */
.option-card-mobile {
  background: rgba(0, 0, 0, 0.25);
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(10px) brightness(0.8);
  -webkit-backdrop-filter: blur(10px) brightness(0.8);
  transition: all 0.3s ease;
}

.option-card-mobile:hover {
  background: rgba(0, 0, 0, 0.35);
  border-color: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(12px) brightness(0.85);
  -webkit-backdrop-filter: blur(12px) brightness(0.85);
}

.option-card-header-mobile {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.option-card-title-mobile {
  font-family: var(--font-body);
  font-size: 1.125rem;
  font-weight: 700;
  color: #FFFFFF;
  line-height: 1.3;
}

.option-card-description-mobile {
  font-family: var(--font-body);
  font-size: 0.8125rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
}

/* ═══════════════════════════════════════════════
   FLECHA DE SCROLL
   ═══════════════════════════════════════════════ */
.scroll-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.1);
  color: #FFFFFF;
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  box-shadow: var(--shadow-md);
  transition: all 0.3s ease;
}

.scroll-arrow:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.4);
  transform: translateY(4px);
}

/* ═══════════════════════════════════════════════
   ANIMACIONES
   ═══════════════════════════════════════════════ */
@keyframes fade-in-right {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slide-in-left {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slide-in-right {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes float-up {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes bounce-subtle {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(6px);
  }
}

.animate-fade-in-right {
  animation: fade-in-right 0.6s ease-out 0.2s both;
}

.animate-slide-in-left {
  animation: slide-in-left 0.7s ease-out;
}

.animate-slide-in-right {
  animation: slide-in-right 0.7s ease-out 0.3s both;
}

.animate-fade-in {
  animation: fade-in 0.6s ease-out 0.4s both;
}

.animate-fade-in-delayed {
  animation: fade-in 0.6s ease-out 0.6s both;
}

.animate-float-up {
  animation: float-up 0.6s ease-out 0.5s both;
}

.animate-bounce-subtle {
  animation: bounce-subtle 2s ease-in-out infinite;
  animation-delay: 1s;
}

/* ═══════════════════════════════════════════════
   RESPONSIVE - MOBILE FIRST
   ═══════════════════════════════════════════════ */

/* Mobile: Stats ocultos, cards verticales */
@media (max-width: 640px) {
  .hero-title {
    font-size: 2rem;
    margin-bottom: 0.75rem;
  }
  
  .hero-subtitle-tagline {
    font-size: 1rem;
  }
  
  .option-card-mobile {
    margin-top: 0;
  }
}

/* Tablet: Cards horizontales compactas */
@media (min-width: 641px) and (max-width: 1023px) {
  .stats-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
  }
  
  .option-card-horizontal {
    grid-template-columns: 1fr 150px;
    min-height: 140px;
  }
  
  .option-card-full {
    min-height: 140px;
  }
  
  .option-card-image-wrapper {
    width: 150px;
  }
  
  .option-card-content,
  .option-card-content-full {
    padding: 1rem;
  }
  
  .option-card-title {
    font-size: 0.9375rem;
  }
  
  .option-card-description {
    font-size: 0.6875rem;
    line-height: 1;
  }
}

/* Desktop Large: Cards un poco más anchas */
@media (min-width: 1280px) {
  .hero-title {
    font-size: 5rem;
  }
  
  .hero-subtitle-tagline {
    font-size: 1.25rem;
  }
  
  .option-card-horizontal {
    grid-template-columns: 1fr 200px;
    min-height: 180px;
  }
  
  .option-card-full {
    min-height: 180px;
  }
  
  .option-card-image-wrapper {
    width: 200px;
  }
  
  .option-card-content,
  .option-card-content-full {
    padding: 1.5rem;
  }
  
  .option-card-title {
    font-size: 1.125rem;
  }
  
  .option-card-description {
    font-size: 0.8125rem;
  }
}

/* ═══════════════════════════════════════════════
   UTILIDADES
   ═══════════════════════════════════════════════ */
/* Screen reader only - Oculto visualmente pero accesible para SEO y screen readers */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Featured Properties Grid - Mantener para sección B2C */
.featured-properties-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 3rem;
}

@media (min-width: 640px) {
  .featured-properties-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .featured-properties-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

/* Mobile horizontal scroll */
@media (max-width: 639px) {
  .featured-properties-grid {
    display: flex;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    scroll-snap-type: x mandatory;
    gap: 1rem;
    padding: 0 1rem;
    margin: 0 -1rem 3rem -1rem;
  }
  
  .featured-properties-grid::-webkit-scrollbar {
    display: none;
  }
  
  .featured-properties-grid > div {
    flex: 0 0 280px;
    scroll-snap-align: start;
  }
}

</style>
